import { Link } from "react-router";
import { Wordmark } from "@/components/Wordmark";
import { TrustBanner } from "@/components/TrustBanner";
import { LANDING } from "@/constants/testIds";
import { ArrowRight, CircleCheck } from "lucide-react";

export default function Landing() {
  return (
    <div data-testid={LANDING.container} className="min-h-screen bg-white">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 fs-navy-bg" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-6 pt-10 pb-20 sm:pt-16 sm:pb-28 text-white">
          <div className="flex items-center justify-between">
            <Wordmark className="text-lg text-white [&_span]:text-blue-400" />
            <Link
              data-testid={LANDING.signInCta}
              to="/signin"
              className="inline-flex items-center gap-1.5 text-sm text-white/80 hover:text-white transition-colors"
            >
              Sign in <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="mt-16 sm:mt-24 max-w-3xl">
            <div className="text-xs uppercase tracking-[0.28em] text-blue-300">
              Secure client portal
            </div>
            <h1 className="mt-4 font-outfit font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Install your First Signal chat agent <span className="text-blue-400">without sharing your website password.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-300 max-w-2xl leading-relaxed">
              Launchpad is the trusted portal your First Signal team uses to
              prepare, install, verify and manage your chat-agent widget. You
              stay in control the entire time.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-3">
              <Link
                to="/signin"
                className="fs-btn-primary inline-flex items-center gap-2 px-5 py-3 rounded-md font-medium"
              >
                Access your Launchpad
                <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="#how"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-md border border-white/20 text-white/90 hover:bg-white/5 transition-colors"
              >
                See how it works
              </a>
            </div>

            <div className="mt-8 flex flex-wrap gap-4 text-sm text-slate-300">
              {["No CMS password required", "Client-approved go-live", "Full audit trail"].map((t) => (
                <div key={t} className="inline-flex items-center gap-1.5">
                  <CircleCheck className="w-4 h-4 text-blue-400" />
                  {t}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Trust */}
      <section id="how" className="max-w-7xl mx-auto px-6 py-16 sm:py-24">
        <div className="max-w-2xl">
          <div className="text-xs uppercase tracking-[0.28em] text-blue-600">Trust promise</div>
          <h2 className="mt-2 font-outfit text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
            Access is limited, logged, and revocable.
          </h2>
          <p className="mt-3 text-slate-600 leading-relaxed">
            Launchpad is designed so you approve every step. We only ever ask
            for what's absolutely necessary to install a single, prepared
            widget.
          </p>
        </div>
        <div className="mt-10">
          <TrustBanner />
        </div>
      </section>

      {/* How it works */}
      <section className="bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-16 sm:py-20">
          <div className="grid gap-8 sm:grid-cols-2">
            {[
              { n: "01", t: "Accept your secure invitation", d: "Your First Signal team invites you by email. No account setup, no passwords." },
              { n: "02", t: "Confirm your site details", d: "We detect (or you confirm) your platform — WordPress, Shopify, Wix, Webflow, Squarespace, GTM, or custom." },
              { n: "03", t: "Authorize what will be installed", d: "You review the exact widget, the permission it needs, and the limits before you approve." },
              { n: "04", t: "Verify then approve go-live", d: "We verify the widget is installed. Then you press Approve & Go Live. You can revoke anytime." },
            ].map((s) => (
              <div key={s.n} className="fs-card p-6">
                <div className="text-xs uppercase tracking-[0.28em] text-slate-500">Step {s.n}</div>
                <div className="mt-2 font-outfit text-xl font-semibold text-slate-900">{s.t}</div>
                <p className="mt-2 text-slate-600 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-8 flex flex-wrap items-center justify-between gap-3 text-sm text-slate-500">
          <Wordmark className="text-sm" />
          <div>Launchpad Core — Phase 1</div>
        </div>
      </footer>
    </div>
  );
}
